import { useEffect, useMemo, useRef } from "react";
import axios, { AxiosRequestConfig } from "axios";
import { axiosInstance } from "./axiosWrapper";
import type { TypedAxiosInstance } from "./axiosShared";

/**
 * Returns an axiosWrapper-compatible client bound to the calling component's lifetime.
 *
 * Every request made through the returned client is tagged with an AbortSignal that fires when the
 * component unmounts. On unmount, in-flight requests are aborted, and — because the request was
 * cancelled by *this* component going away — the returned promise is left permanently pending so the
 * caller's `.then`/`await` continuation never runs. That stops stale continuations from mutating shared
 * global state (e.g. parsing schedule 1's data into the gantt singleton that schedule 2 now owns) with
 * zero changes to call-site error handling.
 *
 * A caller that passes its own `config.signal` opts out: we respect that signal and never swallow its
 * errors, so component-lifetime cancellation and caller-managed cancellation don't fight.
 *
 * Usage — swap `axiosWrapper` for the hook result; the typed-response API is identical:
 *   const api = useAbortableApi();
 *   const res = await api.post<GetResourcesForScheduleResponse>(url, body);
 */
export function useAbortableApi(): TypedAxiosInstance {
  // One controller per mount. Recreated only if the previous one is already aborted (covers a
  // StrictMode remount reusing the same ref), never on ordinary re-renders or reloads.
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      controllerRef.current?.abort();
    };
  }, []);

  return useMemo<TypedAxiosInstance>(() => {
    const unmountSignal = (): AbortSignal => {
      if (!controllerRef.current || controllerRef.current.signal.aborted) {
        controllerRef.current = new AbortController();
      }
      return controllerRef.current.signal;
    };

    // Injects the unmount signal (unless the caller supplied its own), then swallows an
    // unmount-triggered cancellation into a never-settling promise.
    const request = <T>(
      make: (config: AxiosRequestConfig) => Promise<T>,
      config: AxiosRequestConfig | undefined,
    ): Promise<T> => {
      if (config?.signal) {
        return make(config);
      }

      const signal = unmountSignal();
      return make({ ...config, signal }).catch((error: unknown) => {
        if (signal.aborted && axios.isCancel(error)) {
          // The component unmounted — keep the caller's continuation from ever running.
          return new Promise<T>(() => {});
        }
        return Promise.reject(error);
      });
    };

    return {
      get: (url, config) => request((c) => axiosInstance.get(url, c), config),
      post: (url, data, config) => request((c) => axiosInstance.post(url, data, c), config),
      put: (url, data, config) => request((c) => axiosInstance.put(url, data, c), config),
      patch: (url, data, config) => request((c) => axiosInstance.patch(url, data, c), config),
      delete: (url, config) => request((c) => axiosInstance.delete(url, c), config),
    };
  }, []);
}

/**
 * Lower-level primitive: an AbortSignal that fires when the component unmounts. Use this when you make
 * requests through the plain `axiosWrapper` (or any fetch/async work) and just want a lifetime-scoped
 * signal to pass along: `axiosWrapper.post(url, body, { signal: useUnmountSignal() })`.
 */
export function useUnmountSignal(): AbortSignal {
  const controllerRef = useRef<AbortController | null>(null);
  if (!controllerRef.current) {
    controllerRef.current = new AbortController();
  }

  useEffect(() => {
    const controller = controllerRef.current;
    return () => controller?.abort();
  }, []);

  return controllerRef.current.signal;
}
